import React, { useEffect, useState } from "react";
import ListView from "../../../utils/Custom/listView";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { SearchToggleButton } from "../../../utils/Custom/Button";
import { AutoCompletes, DateInput } from "../../../utils/Custom/custominput";
import ExcelDownload from "../../../utils/commonComponent/ExcelDownload";
import { getFormattedDates } from "../../../utils/utils";
import { getPermissions } from "../rbac/hasAccess";

const headers = [ "id", "Client", "address", "poNumber", "jobNumber", "inspectionLocation", "inspectionDates", "inspectionNumber", "state", "Status"];

const notNeededColumn = ["id", "assigneuserID"];

const InspectionCallRegisterReport = (props) => {
  const permissions = getPermissions(props.moduleId);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedInspectorId, setSelectedInspectorId] = useState(null);
  const [inspectorList, setInspectorList] = useState([]);
  const [from, setFromDate] = useState(null);
  const [to, setToDate] = useState(null);

  const [ReportList, setReportList] = useState([]);
  const [summary, setSummary] = useState({
    totalJobs: 0,
    totalInspections: 0,
    completedInspections: 0,
    pendingInspections: 0,
  });
  const [searched, setSearched] = useState(false);
  // Pagination
  const [totalRows, setTotalRows] = useState(0);

  const [currentPage, setCurrentPage] = useState(() =>
    Number(sessionStorage.getItem("ListPageCount") || 0)
  );
  const [rowsPerPage, setRowsPerPage] = useState(() =>
    Number(sessionStorage.getItem("ListRowsPerPage") || 30)
  );

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);

    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const fetchInspectorList = async () => {
    try {
      const query = `SELECT u.id, emp.name FROM users u LEFT JOIN employees emp ON u.employeeId = emp.id WHERE u.status = 1 AND u.type = 2 ORDER BY emp.name ASC`;
      const res = await fetchQuery(query);
      setInspectorList(res || []);
    } catch (error) {
      console.error("Error fetching inspector list:", error);
    }
  };

  // Treat NULL or the MySQL zero-date as "not yet inspected"
  const inspectionDoneExpr = `ir.inspectionDates IS NOT NULL`;

  const getWhereClause = () => {
    let whereConditions = "";

    if (from != null && to != null) {
      const { fromDate, toDate } = getFormattedDates(from, to);
      whereConditions += whereConditions ? " AND" : " WHERE";
      whereConditions += ` DATE(ir.inspectionDates) BETWEEN '${fromDate}' AND '${toDate}'`;
    }

    if (selectedInspectorId != null) {
      whereConditions += whereConditions ? " AND" : " WHERE";
      whereConditions += ` jri.assigneuserID = ${selectedInspectorId}`;
    }

    if (selectedStatus == 1) {
      // Completed
      whereConditions += whereConditions ? " AND" : " WHERE";
      whereConditions += ` ${inspectionDoneExpr}`;
    } else if (selectedStatus == 2) {
      // Pending
      whereConditions += whereConditions ? " AND" : " WHERE";
      whereConditions += ` NOT ${inspectionDoneExpr}`;
    }

    return whereConditions;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT
        ir.id AS id,
        ir.inspectorId,
        c.companyName AS Client,
        addr.address AS address,
        jr.poNumber,
        jr.jobNumber,
        jr.inspectionLocation,
        ir.inspectionDates,
        jri.inspectionNumber,
        jr.state,
        CASE WHEN ${inspectionDoneExpr} THEN 'Completed' ELSE 'Pending' END AS Status
        FROM IR ir
        LEFT JOIN jobRegisterInspection jri ON ir.jobRegisterInspectionId = jri.id
        LEFT JOIN jobRegister jr ON jri.jobRegisterId = jr.id
        LEFT JOIN clients c ON jr.clientId = c.id
        LEFT JOIN addresses addr ON jr.address = addr.id
        ${whereClause}
        ORDER BY ir.id DESC;`
      .trim()
      .replace(/\s+/g, " ");
  };

  const getSummaryQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT
        COUNT(DISTINCT jr.id) AS totalJobs,
        COUNT(ir.id) AS totalInspections,
        SUM(CASE WHEN ${inspectionDoneExpr} THEN 1 ELSE 0 END) AS completedInspections,
        SUM(CASE WHEN NOT ${inspectionDoneExpr} THEN 1 ELSE 0 END) AS pendingInspections
        FROM IR ir
        LEFT JOIN jobRegisterInspection jri ON ir.jobRegisterInspectionId = jri.id
        LEFT JOIN jobRegister jr ON jri.jobRegisterId = jr.id
        ${whereClause};`
      .trim()
      .replace(/\s+/g, " ");
  };

  const getCountQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT COUNT(ir.id) AS totalRows
        FROM IR ir
        LEFT JOIN jobRegisterInspection jri ON ir.jobRegisterInspectionId = jri.id
        LEFT JOIN jobRegister jr ON jri.jobRegisterId = jr.id
        ${whereClause};`
      .trim()
      .replace(/\s+/g, " ");
  };

  const fetchReportData = async () => {
    try {
      const [dataResult, summaryResult, totalRows] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getSummaryQuery()),
        fetchQuery(getCountQuery()),
      ]);
      setReportList(dataResult || []);
      setTotalRows(totalRows?.[0]?.totalRows || 0);
      setSummary({
        totalJobs: summaryResult?.[0]?.totalJobs || 0,
        totalInspections: summaryResult?.[0]?.totalInspections || 0,
        completedInspections: summaryResult?.[0]?.completedInspections || 0,
        pendingInspections: summaryResult?.[0]?.pendingInspections || 0,
      });
    } catch (error) {
      console.error("Error fetching inspection completion report:", error);
    }
  };

  const handleSearch = () => {
    setSearched(true);
    fetchReportData();
  };

  const clearSearch = () => {
    setSearched(false);
    setReportList([]);
    setSummary({
      totalJobs: 0,
      totalInspections: 0,
      completedInspections: 0,
      pendingInspections: 0,
    });
    setFromDate(null);
    setToDate(null);
    setSelectedInspectorId(null);
    setSelectedStatus(null);
  };

  useEffect(() => {
    fetchInspectorList();
  }, []);

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2 my-3">
        <div className="w-full">
          <DateInput
            id="from-date"
            label="From Date"
            value={from}
            onChange={(val) => {
              setFromDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div className="w-full">
          <DateInput
            id="to-date"
            label="To Date"
            value={to}
            onChange={(val) => {
              setToDate(val);
              setSearched(false);
            }}
          />
        </div>
        <div className="w-full">
          <AutoCompletes
            label="Inspector"
            options={inspectorList.map((emp) => ({ id: emp.id, label: emp.name }))}
            value={selectedInspectorId}
            onChange={(val) => {
              setSelectedInspectorId(val);
              setSearched(false);
            }}
            isWidth
            valueKey="id"
            labelKey="label"
          />
        </div>
        <div className="w-full">
          <AutoCompletes
            label="Status"
            options={[
              { id: 1, label: "Completed" },
              { id: 2, label: "Pending" },
            ]}
            value={selectedStatus}
            onChange={(val) => {
              setSelectedStatus(val);
              setSearched(false);
            }}
            isWidth
            valueKey="id"
            labelKey="label"
          />
        </div>
        <div>
          <SearchToggleButton
            searched={searched}
            onSearch={handleSearch}
            onClear={clearSearch}
          />
        </div>
        {ReportList?.length > 0 &&
          (permissions.excelDownload || permissions.allAccess) && (
            <div>
              <ExcelDownload
                finalData={ReportList}
                notNeededColumn={notNeededColumn}
              />
            </div>
          )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-3">
        <div className="bg-blue-50 rounded-md shadow p-3 text-center">
          <div className="text-xs text-black font-bold">Total Jobs</div>
          <div className="text-xl font-semibold text-blue-700">
            {summary.totalJobs}
          </div>
        </div>
        <div className="bg-blue-50 rounded-md shadow p-3 text-center">
          <div className="text-xs text-black font-bold">Total Inspections (IR)</div>
          <div className="text-xl font-semibold text-blue-700">
            {summary.totalInspections}
          </div>
        </div>
        <div className="bg-green-50 rounded-md shadow p-3 text-center">
          <div className="text-xs text-black font-bold">Completed Inspections</div>
          <div className="text-xl font-semibold text-green-700">
            {summary.completedInspections}
          </div>
        </div>
        <div className="bg-red-50 rounded-md shadow p-3 text-center">
          <div className="text-xs text-black font-bold">Pending</div>
          <div className="text-xl font-semibold text-red-700">
            {summary.pendingInspections}
          </div>
        </div>
      </div>

      <ListView
        headers={headers}
        data={ReportList}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        totalRows={totalRows}
        onPaginationChange={handlePaginationChange}
      />
    </>
  );
};

export default InspectionCallRegisterReport;